/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Sync to Main section - product owner workflow for syncing velocity to main
 */

import React from 'react';
import { RefreshCw, GitMerge, AlertTriangle, CheckCircle, Circle, Loader } from 'lucide-react';
import type { SyncToMainAnalysis, SyncToMainDecision, SourceControlProvider } from './types';

interface SyncToMainSectionProps {
  syncToMainAnalysis: SyncToMainAnalysis | null;
  syncToMainDecision: SyncToMainDecision | null;
  loadingSyncToMainAnalysis: boolean;
  onSyncToMain: () => void;
  syncingToMain: boolean;
  onRefreshAnalysis: () => void;
  isSourceControlConfigured: boolean;
  sourceControlProvider: SourceControlProvider;
  /** Target branch name from organization settings (defaults to 'main') */
  defaultBranch?: string;
}

export function SyncToMainSection({
  syncToMainAnalysis,
  syncToMainDecision,
  loadingSyncToMainAnalysis,
  onSyncToMain,
  syncingToMain,
  onRefreshAnalysis,
  isSourceControlConfigured,
  sourceControlProvider,
  defaultBranch = 'main'
}: SyncToMainSectionProps) {
  return (
    <div className="p-4 space-y-2 border-b border-gray-200">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
        Sync to {defaultBranch}
      </div>
      
      {/* Analysis Summary */}
      {loadingSyncToMainAnalysis ? (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="animate-spin text-gray-400" />
          <span className="text-xs text-gray-500 ml-2">Analyzing...</span>
        </div>
      ) : syncToMainAnalysis ? (
        <>
          {syncToMainAnalysis.aheadOfMain > 0 ? (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-start gap-2">
                <GitMerge size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-emerald-900">
                    Ready to Sync
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    {syncToMainAnalysis.aheadOfMain} commit{syncToMainAnalysis.aheadOfMain !== 1 ? 's' : ''} ahead of {defaultBranch} 
                    ({syncToMainAnalysis.productDataFiles.length} product file{syncToMainAnalysis.productDataFiles.length !== 1 ? 's' : ''})
                  </p>
                  {syncToMainDecision?.warnings && syncToMainDecision.warnings.length > 0 && (
                    <div className="mt-2 text-xs text-yellow-700">
                      {syncToMainDecision.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-1">
                          <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
                          <span>{w}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500">
                <CheckCircle size={14} />
                <span className="text-sm">{defaultBranch} is up to date</span>
              </div>
            </div>
          )}
          
          {/* Conflict Warning */}
          {syncToMainAnalysis.hasConflicts && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-red-900">
                    Merge Conflicts Detected
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    {syncToMainAnalysis.conflictFiles.length} file{syncToMainAnalysis.conflictFiles.length !== 1 ? 's' : ''} have conflicts with {defaultBranch}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-400">
            <Circle size={14} />
            <span className="text-sm">No analysis available</span>
          </div>
        </div>
      )}
      
      {/* Sync Actions */}
      <div className="flex gap-2">
        <button
          onClick={onSyncToMain}
          disabled={
            syncingToMain || 
            !isSourceControlConfigured || 
            !syncToMainAnalysis || 
            syncToMainAnalysis.aheadOfMain === 0 ||
            syncToMainAnalysis.hasConflicts
          }
          className="flex-1 btn-primary text-sm py-2 flex items-center justify-center gap-1"
          title={!isSourceControlConfigured ? `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required` : 
                 syncToMainAnalysis?.hasConflicts ? 'Resolve conflicts first' :
                 syncToMainAnalysis?.aheadOfMain === 0 ? 'No changes to sync' :
                 `Sync product data to ${defaultBranch} branch`}
        >
          {syncingToMain ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <GitMerge size={14} />
          )}
          Sync to {defaultBranch}
          {syncToMainAnalysis && syncToMainAnalysis.aheadOfMain > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">
              {syncToMainAnalysis.aheadOfMain}
            </span>
          )}
        </button>
        
        <button
          onClick={onRefreshAnalysis}
          disabled={loadingSyncToMainAnalysis}
          className="btn-secondary text-sm py-2 px-3"
          title="Refresh analysis"
        >
          <RefreshCw size={14} className={loadingSyncToMainAnalysis ? 'animate-spin' : ''} />
        </button>
      </div>
    </div>
  );
}

